//@flow
import {Link} from 'react-router-dom';
import React, {useState} from 'react';
import SwitchableSection from '../../../../../elements/SwitchableSection';
import {Button, makeStyles} from '@material-ui/core';
import {Add} from '@material-ui/icons';
import {colors} from '../../../../../assets/styles/colors';
import BannerPreview from './PopupBannerPreview';
import {
  useInitializePopupBanner,
  usePopupBannerActions,
} from '../../../utils/popup-banner';
import {useToastActions} from '../../../../toast-alert/utils';
import PopupBannerItem from './PopupBannerItem';
import {useIsFeatureInTier} from '../../../../../utils/component';

import type {PopupBanner as PopupBannerType} from '../../../../../entities';
import {useTierRestrictionActions} from '../../../../TierRestriction/utils';

const useStyles = makeStyles(theme => ({
  addBannerButton: {
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    color: colors.grey500,
    border: `2px dashed ${colors.grey500}`,
    borderRadius: '.75rem',
    paddingBlock: '.85rem',
  },
  bannerList: {
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const MAX_POPUP_BANNERS = 5;

export default function PopupBanner() {
  const classes = useStyles();

  const popupBanners = useInitializePopupBanner();

  const [bannerSelected, setBannerSelected] = useState<PopupBannerType | null>(
    null
  );

  const {deleteBanner, setEnabledBanner} = usePopupBannerActions();
  const {showToast} = useToastActions();

  const hasPopupBannerFeature = useIsFeatureInTier('STORE_POP_UP');
  const {showTierRestrictionModal} = useTierRestrictionActions();

  const chipLabel = hasPopupBannerFeature ? 'Nuevo' : 'PRO +';

  const handleDeleteBanner = async (bannerId: string) => {
    try {
      await deleteBanner(bannerId);
      showToast({
        type: 'success',
        title: 'Éxito',
        message: 'Banner eliminado correctamente',
        duration: 3000,
      });
    } catch (error) {
      const errorMessage =
        error?.error?.message || 'Ocurrió un error al eliminar el banner';
      showToast({
        type: 'danger',
        title: 'Error',
        message: errorMessage,
        duration: 5000,
      });
    }
  };

  const handleToggleEnabled = async (bannerId: string, enabled: boolean) => {
    try {
      await setEnabledBanner(bannerId, enabled);
    } catch (error) {
      const errorMessage =
        error?.error?.message ||
        'Ocurrió un error al actualizar el estado del banner';
      showToast({
        type: 'danger',
        title: 'Error',
        message: errorMessage,
        duration: 5000,
      });
    }
  };

  const canAddBanner = popupBanners?.length < MAX_POPUP_BANNERS;

  return (
    <SwitchableSection
      id="popup-banner"
      title="Banner Popup"
      subtitle={`Se pueden agregar hasta ${MAX_POPUP_BANNERS} banners: ${popupBanners?.length}/${MAX_POPUP_BANNERS}`}
      chipLabel={chipLabel}
      hideSwitch
    >
      <Button
        fullWidth
        className={classes.addBannerButton}
        to="/my-store/pages/home-page/popup-banner"
        component={Link}
        onClick={e => {
          if (!hasPopupBannerFeature) {
            e.preventDefault();
            showTierRestrictionModal();
          }
        }}
        disabled={!canAddBanner}
      >
        <Add />
        Agregar nuevo
      </Button>

      <section className={classes.bannerList}>
        {popupBanners?.map((banner, index) => {
          return (
            <PopupBannerItem
              key={banner.id}
              banner={banner}
              featureDisabled={!hasPopupBannerFeature}
              onDelete={() => {
                handleDeleteBanner(banner.id);
              }}
              onPreview={() => {
                setBannerSelected(banner);
              }}
              onToggleEnabled={() => {
                handleToggleEnabled(banner.id, !banner.enabled);
              }}
            />
          );
        })}
      </section>

      {bannerSelected && (
        <BannerPreview
          banner={bannerSelected}
          onClose={() => {
            setBannerSelected(null);
          }}
        />
      )}
    </SwitchableSection>
  );
}
